import React, { useState } from 'react';
import { Table, Button, Modal, Form, Input, message, Space, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';

const Subjects = () => {
    const { t, i18n } = useTranslation();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingSubject, setEditingSubject] = useState(null);
    const [form] = Form.useForm();
    const queryClient = useQueryClient();

    const { data: subjectsData, isLoading } = useQuery('subjects', async () => {
        const response = await api.get('/subjects');
        return response.data.data;
    }, {
        refetchOnMount: 'always',
        refetchOnWindowFocus: false
    });

    const createMutation = useMutation(
        (data) => api.post('/subjects', data),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('subjects');
                message.success(t('createdSuccessfully'));
                setIsModalVisible(false);
                form.resetFields();
            },
            onError: (error) => {
                message.error(error.response?.data?.message || 'Failed to create subject');
            },
        }
    );

    const updateMutation = useMutation(
        ({ id, data }) => api.put(`/subjects/${id}`, data),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('subjects');
                message.success(t('updatedSuccessfully'));
                setIsModalVisible(false);
                setEditingSubject(null);
                form.resetFields();
            },
            onError: (error) => {
                message.error(error.response?.data?.message || 'Failed to update subject');
            },
        }
    );

    const deleteMutation = useMutation(
        (id) => api.delete(`/subjects/${id}`),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('subjects');
                message.success(t('deletedSuccessfully'));
            },
            onError: (error) => {
                message.error(error.response?.data?.message || 'Failed to delete subject');
            },
        }
    );

    const handleSubmit = (values) => {
        if (editingSubject) {
            updateMutation.mutate({ id: editingSubject._id, data: values });
        } else {
            createMutation.mutate(values);
        }
    };

    const handleEdit = (record) => {
        setEditingSubject(record);
        form.setFieldsValue(record);
        setIsModalVisible(true);
    };

    const handleDelete = (id) => {
        deleteMutation.mutate(id);
    };

    const columns = [
        {
            title: t('subjectName'),
            dataIndex: 'name',
            key: 'name',
            render: (name) => name?.[i18n.language] || name?.en || '-',
        },
        {
            title: t('createdAt'),
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (date) => new Date(date).toLocaleDateString(),
        },
        {
            title: t('actions'),
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                        size="small"
                    />
                    <Popconfirm
                        title={t('confirmDelete')}
                        description={t('actionCannotBeUndone')}
                        onConfirm={() => handleDelete(record._id)}
                        okText={t('yes')}
                        cancelText={t('no')}
                    >
                        <Button
                            icon={<DeleteOutlined />}
                            danger
                            size="small"
                        />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <div className="page-header">
                <h1>{t('subjects')}</h1>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => {
                        setEditingSubject(null);
                        form.resetFields();
                        setIsModalVisible(true);
                    }}
                >
                    {t('addSubject')}
                </Button>
            </div>

            <Table
                columns={columns}
                dataSource={subjectsData}
                rowKey="_id"
                loading={isLoading}
                pagination={{ pageSize: 10 }}
            />

            <Modal
                title={editingSubject ? t('editSubject') : t('addSubject')}
                open={isModalVisible}
                onCancel={() => {
                    setIsModalVisible(false);
                    setEditingSubject(null);
                    form.resetFields();
                }}
                footer={null}
            >
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    <Form.Item
                        name={['name', 'en']}
                        label={t('subjectName') + ' (English)'}
                        rules={[{ required: true, message: 'Please enter English subject name' }]}
                    >
                        <Input placeholder="e.g., English, Mathematics, Science" />
                    </Form.Item>

                    <Form.Item
                        name={['name', 'ar']}
                        label={t('subjectName') + ' (العربية)'}
                        rules={[{ required: true, message: 'Please enter Arabic subject name' }]}
                    >
                        <Input placeholder="مثال: الإنجليزية، الرياضيات، العلوم" />
                    </Form.Item>

                    <Form.Item
                        name={['name', 'ku']}
                        label={t('subjectName') + ' (کوردی)'}
                        rules={[{ required: true, message: 'Please enter Kurdish subject name' }]}
                    >
                        <Input placeholder="نموونە: ئینگلیزی، ماتماتیک، زانست" />
                    </Form.Item>

                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={createMutation.isLoading || updateMutation.isLoading}>
                                {editingSubject ? t('update') : t('create')}
                            </Button>
                            <Button onClick={() => {
                                setIsModalVisible(false);
                                setEditingSubject(null);
                                form.resetFields();
                            }}>
                                {t('cancel')}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default Subjects;
